import React, { useEffect, useRef, useState } from "react";
import { useForm, UseFormGetValues, UseFormSetValue, FieldValues, Path, PathValue } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { ContentViewport, SinglePanel, Utility } from "../../app/app-layout";
import { useAccessControl } from "../../app/access-control-context";
import { InputField } from "../../components/forms/input-field";
import { TextAreaField } from "../../components/forms/text-area-field";
import { BrowserAutocompleteDecoyFields } from "../../components/forms/browser-autocomplete-decoy-fields";
import { useSuppressBrowserAutocomplete } from "../../components/forms/suppress-browser-autocomplete";
import { useCenterWebsite } from "../../hooks/use-center/use-center-website";
import { LoadingOverlay } from "../../components/loading-overlay/loading-overlay";
import { LoadingButton } from "../../components/loading-button/loading-button";
import { SimpleTable, SimpleTableHeader } from "../../components/table/simple-table";
import { IconButton } from "../../components/buttons/icon-button";
import { useReferenceData } from "../../hooks/use-reference-data";
import { dollarsSchema, isValidUrl } from "../../components/forms/form-helpers";
import { CenterWebsite, DISCOUNT_TYPES } from "../../hooks/use-center";
import { useNavigationConfirmation } from "../../hooks/use-navigation-confirmation";
import { Confirm, ConfirmHandle } from "../../components/confirm/confirm";
import * as Permissions from "../../data/permissions";

const centerWebsiteFormSchema = z.object({
  shortDescription: z.string(),
  longDescription: z.string(),
  areas: z.string(),
  schools: z.string(),
  directions: z.string(),
  alumniDestination: z.string(),
  discountType: z.string(),
  discountValue: dollarsSchema,
  socialMedia: z.array(
    z.object({ id: z.string().optional(), url: z.string().url().or(z.literal("")), name: z.string() })
  )
});

type CenterWebsiteFormSchema = z.infer<typeof centerWebsiteFormSchema>;

const WEBSITE_TEXT_LIMITS = {
  shortDescription: 415,
  longDescription: 2000,
  areas: 520,
  schools: 520,
  alumniDestination: 520,
  directions: 88
} as const;

type WebsiteTextField = keyof typeof WEBSITE_TEXT_LIMITS;

const CharacterCount = ({ name, value }: { name: WebsiteTextField; value?: string }) => (
  <div className="form-text" data-testid={`character-count-${name}`}>
    {`${value?.length ?? 0}/${WEBSITE_TEXT_LIMITS[name]}`}
  </div>
);

// Card columns are a horizontal flex row, so a count next to a field
// shrinks the box and sits beside it. This keeps the box full width.
const FieldWithCount = ({ children }: { children: React.ReactNode }) => (
  <div className="d-flex flex-column w-100">{children}</div>
);

// Custom URL input component with its own state management
const UrlInput = <T extends FieldValues>({
  displayName,
  fieldName,
  getValues,
  setValue,
  disabled = false
}: {
  displayName: string;
  fieldName: Path<T>;
  getValues: UseFormGetValues<T>;
  setValue: UseFormSetValue<T>;
  disabled?: boolean;
}) => {
  const [inputValue, setInputValue] = useState("" as PathValue<T, Path<T>>);
  const [error, setError] = useState<string | null>(null);
  const browserAutocompleteSuppression = useSuppressBrowserAutocomplete(undefined, { type: "url" });

  // Initialize from form value
  useEffect(() => {
    const value = getValues(fieldName as Path<T>);
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [fieldName, getValues]);

  // Update form value when input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value as PathValue<T, Path<T>>;
    setInputValue(newValue);

    // Clear error while typing
    setError(null);
    // Update form value without validation
    setValue(fieldName, newValue, { shouldValidate: false });
  };

  // Validate on blur using Zod
  const handleBlur = () => {
    // Validate URL only if not empty
    if (inputValue) {
      try {
        // Create a Zod schema for URL validation
        const urlSchema = z.string().url();
        // Validate the URL
        urlSchema.parse(inputValue);
        // If validation passes, clear any error
        setError(null);
      } catch (validationError) {
        // If validation fails, set the error message
        setError("Invalid URL");
      }
    } else {
      setError(null);
    }
    // Update form value
    setValue(fieldName, inputValue, { shouldDirty: true, shouldValidate: false });
  };

  return (
    <>
      <input
        type="url"
        className={`form-control off-brand-input ${error ? "is-invalid" : ""}`}
        data-testid="social-media-link-textbox"
        aria-label={`${displayName} URL`}
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        readOnly={browserAutocompleteSuppression.readOnly}
        onFocus={browserAutocompleteSuppression.onFocus}
        autoComplete={browserAutocompleteSuppression.autoComplete}
        autoCorrect={browserAutocompleteSuppression.autoCorrect}
        spellCheck={browserAutocompleteSuppression.spellCheck}
        data-1p-ignore={browserAutocompleteSuppression["data-1p-ignore"]}
        data-lpignore={browserAutocompleteSuppression["data-lpignore"]}
      />
      {error && <div className="invalid-feedback">{error}</div>}
    </>
  );
};

export const CenterWebsiteScreen: React.FC = () => {
  const { activeCenterId, hasPermission } = useAccessControl();
  const canEditWebsite = !!hasPermission?.(Permissions.CENTER_EDIT_WEBSITE);

  const { website, isLoading, websiteMutator } = useCenterWebsite(activeCenterId);
  const { socialMediaTypes } = useReferenceData();
  const confirmCloseWithoutSavingModalSingleTab = useRef<ConfirmHandle>(null);
  const defaultValues = {
    shortDescription: "",
    longDescription: "",
    areas: "",
    schools: "",
    directions: "",
    alumniDestination: "",
    discountType: DISCOUNT_TYPES.DOLLAR,
    discountValue: 0,
    socialMedia: []
  };

  const websiteForm = useForm<CenterWebsiteFormSchema>({
    mode: "onTouched",
    resolver: zodResolver(centerWebsiteFormSchema),
    defaultValues,
    disabled: !canEditWebsite
  });

  const {
    register,
    reset,
    watch,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors, isDirty }
  } = websiteForm;
  const websiteValues = watch();

  useEffect(() => {
    reset(
      centerWebsiteFormSchema.parse({
        ...defaultValues,
        ...website,
        discountType: website?.discountType || DISCOUNT_TYPES.DOLLAR,
        discountValue: website?.discountValue || defaultValues.discountValue
      })
    );
  }, [website]);

  const onCancel = () => {
    reset(
      centerWebsiteFormSchema.parse({
        ...defaultValues,
        ...website,
        discountType: website?.discountType || DISCOUNT_TYPES.DOLLAR,
        discountValue: website?.discountValue || defaultValues.discountValue
      })
    );
  };

  const onSubmit = handleSubmit(data => {
    websiteMutator.mutate(data as CenterWebsite, {
      onSuccess: () => {
        window.toasts.add({
          type: "success",
          content: "Published website updates successfully!",
          duration: 3000
        });
      },
      onError: (error: unknown) => {
        window.toasts.add({
          type: "danger",
          content: "Failed to publish website updates.",
          duration: 30000
        });
        // eslint-disable-next-line no-console
        console.error("Submission error:", error);
      }
    });
  });

  const discountType = watch("discountType");

  useEffect(() => {
    if (!discountType) {
      setValue("discountType", DISCOUNT_TYPES.DOLLAR);
    }
  }, [discountType]);

  const { blocker } = useNavigationConfirmation({ confirmNavigation: isDirty });

  useEffect(() => {
    if (blocker.state === "blocked") {
      confirmCloseWithoutSavingModalSingleTab.current?.confirm(blocker.proceed, () => {
        blocker.reset();
      });
    }
  }, [blocker.state]);

  const socialMediaSpecs: SimpleTableHeader<[string, string]>[] = [
    {
      renderKey: "platform",
      name: "social platform",
      render: ([displayName, className]: [string, string]) => (
        <div className="social-media">
          <span aria-hidden={true} className={className} />
          {displayName}
        </div>
      ),
      tableDataCellProps: { isAction: true, textAlign: "right", className: "td-primary" }
    },
    {
      renderKey: "url",
      name: "url",
      render: ([displayName], index) => {
        return (
          <UrlInput<CenterWebsiteFormSchema>
            displayName={displayName}
            fieldName={`socialMedia.${index}.url`}
            getValues={getValues}
            setValue={setValue}
            disabled={!canEditWebsite}
          />
        );
      },
      tableDataCellProps: { isAction: true }
    },
    {
      renderKey: "action",
      name: "",
      render: ([displayName, className], index) => {
        return (
          <IconButton
            title={`Visit ${displayName}`}
            icon="up-right-from-square"
            enableTooltip
            data-testid={`open-social-media-link-${className.replace(" ", "-")}`}
            className="btn btn-table"
            onClick={() => {
              const url = getValues(`socialMedia.${index}.url`);

              if (url && isValidUrl(url)) {
                window.open(url, "_blank");
              } else {
                window.toasts.add({ type: "warning", content: "not a valid web site address", duration: 5000 });
              }
            }}
          />
        );
      },
      tableDataCellProps: {
        isAction: true,
        textAlign: "right"
      }
    }
  ];

  if (!activeCenterId) {
    return null;
  }

  return (
    <ContentViewport>
      <BrowserAutocompleteDecoyFields />
      <SinglePanel>
        {canEditWebsite && (
          <div className="sticky-top">
            <Utility className="mb-0 justify-content-end">
              <button className="me-2 btn btn-secondary" onClick={onCancel}>
                Cancel
              </button>

              <LoadingButton
                className="btn btn-primary"
                onClick={onSubmit}
                isLoading={websiteMutator.isLoading || isLoading}
                loadingMessage="Publish"
                style={{ width: "150px" }}
              >
                Publish
              </LoadingButton>
            </Utility>
          </div>
        )}

        <Confirm
          ref={confirmCloseWithoutSavingModalSingleTab}
          title="Confirm"
          message="Are you sure you want to leave without saving your changes?"
        />

        <div className="content-pad">
          <LoadingOverlay loading={isLoading || websiteMutator.isLoading}>
            <div className="row">
              <div className="col col-12 col-lg-6 mb-4">
                <div className="card">
                  <div className="card-header">
                    <h2>Website Information</h2>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col col-12">
                        <FieldWithCount>
                          <TextAreaField
                            label="About the Center (Short)"
                            maxLength={WEBSITE_TEXT_LIMITS.shortDescription}
                            disableColClass
                            placeholder="Write a short  description about the center."
                            error={errors.shortDescription}
                            style={{ height: "100px" }}
                            data-testid="about-the-center-short"
                            {...register("shortDescription")}
                          />
                          <CharacterCount name="shortDescription" value={websiteValues.shortDescription} />
                        </FieldWithCount>
                      </div>
                      <div className="col col-12">
                        <FieldWithCount>
                          <TextAreaField
                            label="About the Center (Long)"
                            maxLength={WEBSITE_TEXT_LIMITS.longDescription}
                            disableColClass
                            placeholder="Write a long description about the center."
                            error={errors.longDescription}
                            style={{ height: "200px" }}
                            {...register("longDescription")}
                          />
                          <CharacterCount name="longDescription" value={websiteValues.longDescription} />
                        </FieldWithCount>
                      </div>
                      <div className="col col-12">
                        <FieldWithCount>
                          <TextAreaField
                            label="Areas We Serve"
                            maxLength={WEBSITE_TEXT_LIMITS.areas}
                            disableColClass
                            placeholder="Write a description about areas served."
                            error={errors.areas}
                            style={{ height: "100px" }}
                            {...register("areas")}
                          />
                          <CharacterCount name="areas" value={websiteValues.areas} />
                        </FieldWithCount>
                      </div>
                      <div className="col col-12">
                        <FieldWithCount>
                          <TextAreaField
                            label="Schools We Service"
                            maxLength={WEBSITE_TEXT_LIMITS.schools}
                            disableColClass
                            placeholder="Write a description about schools serviced."
                            error={errors.schools}
                            style={{ height: "100px" }}
                            {...register("schools")}
                          />
                          <CharacterCount name="schools" value={websiteValues.schools} />
                        </FieldWithCount>
                      </div>
                      <div className="col col-12">
                        <FieldWithCount>
                          <TextAreaField
                            label="Directions"
                            maxLength={WEBSITE_TEXT_LIMITS.directions}
                            disableColClass
                            placeholder="Write a description about directions to the center."
                            error={errors.directions}
                            style={{ height: "60px" }}
                            {...register("directions")}
                          />
                          <CharacterCount name="directions" value={websiteValues.directions} />
                        </FieldWithCount>
                      </div>
                      <div className="col col-12">
                        <FieldWithCount>
                          <TextAreaField
                            label="Where Our Graduates Have Gone"
                            maxLength={WEBSITE_TEXT_LIMITS.alumniDestination}
                            disableColClass
                            placeholder="Write a description about where graduates have gone."
                            error={errors.alumniDestination}
                            style={{ height: "100px" }}
                            {...register("alumniDestination")}
                          />
                          <CharacterCount name="alumniDestination" value={websiteValues.alumniDestination} />
                        </FieldWithCount>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col col-12 col-lg-6 mb-4">
                <div className="card">
                  <div className="card-header">
                    <h2>Coupon / Offer</h2>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col col-12">
                        <div className="d-flex">
                          <div className="me-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="discountTypeDollar"
                                value={DISCOUNT_TYPES.DOLLAR}
                                disabled={!canEditWebsite}
                                {...register("discountType")}
                              />
                              <label className="form-check-label" htmlFor="discountTypeDollar">
                                $
                              </label>
                            </div>
                            <div className="form-check flex-grow-1">
                              <input
                                className="form-check-input"
                                type="radio"
                                id="discountTypePercentage"
                                value={DISCOUNT_TYPES.PERCENT}
                                disabled={!canEditWebsite}
                                {...register("discountType")}
                              />
                              <label className="form-check-label" htmlFor="discountTypePercentage">
                                %
                              </label>
                            </div>
                          </div>
                          <div className="w-100">
                            <InputField
                              disableColClass
                              type="number"
                              step=".01"
                              label={`Discount`}
                              error={errors.discountValue}
                              {...register("discountValue", {
                                setValueAs: v => (v === "" ? undefined : parseFloat(v))
                              })}
                              labelClassName="single-line-label mx-5 px-sm-2 bg-transparent"
                              addOnClassName={
                                discountType === DISCOUNT_TYPES.DOLLAR
                                  ? "fas icon fa-dollar-sign"
                                  : "fas icon fa-percent"
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-header">
                    <h2>Social Media Links</h2>
                  </div>
                  <div className="card-body">
                    <SimpleTable
                      tableCollapseScreenSize="sm"
                      tableHasIcons
                      data={socialMediaTypes}
                      headers={socialMediaSpecs}
                      uniqueIdKey="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </LoadingOverlay>
        </div>
      </SinglePanel>
    </ContentViewport>
  );
};

export const centerWebsiteLoader = () => {
  return {
    activeTab: "Centers",
    tabQualifier: "Website"
  };
};
